<!-- EDocumentSaveScreen.vue -->
<script lang="ts" setup>
import { computed, onBeforeMount, reactive, ref, watch } from 'vue';
import RadioButtonGroup from '../../components/Inputs/RadioButtonGroup.vue';
import { IRadioButtonItem } from '../../components/Inputs/RadioButtonGroupModels';
import { AppScreens, ListNames, MenuNameType, Priority } from '../../Enums/ChoiceEnums';
import { DisplayStyle, LayoutOrientation, MessageSectionType, ButtonDisplaySize, TableRowType } from '../../Enums/UiEnums';
import SearchDocumentFolder from '../../components/Search/SearchDocumentFolder.vue';
import Row from '../../components/Section/Row.vue';
import SearchPatient from '../../components/Search/SearchPatient.vue';
import { IListByNameExtended, ISearchDocumentFolderItem, ISearchPatientItem, ISearchPatientKey, ISearchPayerItem, ISearchResourceItem } from '../../lib/Models/SearchModel';
import ActionButtonV2 from '../../components/ActionButtonV2.vue';
import { initPinia } from '../../lib/PiniaMgr';
import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore'
import GeneralDialog from '../../components/GeneralDialog.vue';
import PatientCreateQuick from '../Screens/PatientCreateQuick.vue';
import MessageSection from '../../components/Messages/MessageSection.vue';
import { ActionButtonStyle } from '../../lib/Types/Types';
import SearchResource from '../../components/Search/SearchResource.vue';
import SearchPayer from '../../components/Search/SearchPayer.vue';
import TextInput from '../../components/TextInput.vue';
import { LimitToFilenameCharacters } from '../../lib/Validation';
import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
import SearchUserGroupMultiple from '../../components/Search/SearchUserGroupMultiple.vue';
import { hasSlot } from '../../lib/VueHelpers';
import { combineFilenameAndExt, fileExtensionWithDot } from '../../lib/FileUtils';
import MessageText from '../../components/Messages/MessageText.vue';
import { patientEncountersLast } from '../../lib/Api/Appointment';
import { ICol, ValueStrDelegate } from "../../lib/ChoiceTableHelpers";
import ChoiceTable from "../../components/ChoiceTable.vue";
import { AppointmentPatientEncountersLastItem } from "../../lib/Models/AppointmentModel";


    const formatDetails: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
            const chiefComplaint = row?.chiefComplaint ?? "";
            const comments = row?.comments ?? "";
            if (chiefComplaint.length > 0 && comments.length > 0) {
                return `${chiefComplaint} - ${comments}`;
            } else if (chiefComplaint.length > 0) {
                return chiefComplaint;
            } else if (comments.length > 0) {
                return comments;
            } else {
                return "";
            }
        }
    }

    const encounterColumns = ref<ICol[]>([
        { Name: 'Date', DataField: 'appointmentDate' },
        { Name: 'Resource', DataField: 'resourceName' },
        { Name: 'Location', DataField: 'serviceLocationName' },
        { Name: 'Details', DataField: 'comments', FormatValue: formatDetails }
    ]);

    const props = defineProps<{
        filename?: string,
        filenameExt?: string,               // If not specified, assume ".pdf"
        allowManualExtEntry?: boolean,      // Indicate if the file extension can be entered manually
        selectedMenuType?: MenuNameType,    // Id of the selected menu item
        visibleMenuTypes?: MenuNameType[],  // Which menu items to display.  If not set, all items will be shown
        hidePatientSearch?: boolean,        // For the patient menu, indicate if we need to show the patient search
    }>();

    watch(() => props.filename, (newValue, _oldValue) => {
        compData.filename = newValue;
    }); 
    
    watch(() => props.filenameExt, (newValue, _oldValue) => {
        compData.manualFileExt = newValue;
    });     

    const emits = defineEmits<{
        (e: "filename", value: string): void,
        (e: "selectedMenuType", value: MenuNameType): void
    }>();

    defineExpose({getSaveInfo});

    const compData = reactive<{
        filename?: string,
        manualFileExt?: string,
        menuNameId?: MenuNameType,
        allowCreatePatient?: boolean,
        showCreateNewPatient?: boolean,
        receivedDate?: string,       // No Timezone
        reviewList?: IListByNameExtended[],
        reviewMessage?: string,
        priority: Priority,
        useGroupName?: boolean,
        menuTypeItems: Record<MenuNameType, ISearchItem>,
        showEncounters?: boolean,
        encounterList: AppointmentPatientEncountersLastItem[]
    }>({
        menuNameId: MenuNameType.Patient,
        priority: Priority.Normal,
        menuTypeItems: {
            [MenuNameType.Practice]: {displaySearchError: false, displayFolderError: false},
            [MenuNameType.Patient]: {displaySearchError: false, displayFolderError: false},
            [MenuNameType.Resource]: {displaySearchError: false, displayFolderError: false},
            [MenuNameType.Payer]: {displaySearchError: false, displayFolderError: false}            
        },
        encounterList:[]
    });

    const privilegesStore = usePrivilegesStore(initPinia());

    export interface ISaveInfo 
    {
        entityId?: number, 
        filename: string,
        folderMenuNameId?: MenuNameType, 
        folderMenuId?: number, 
        receivedDate?: string,
        reviewMessage?: string,
        reviewList?: {identifier: string, optionalIdentifier: string}[],
        priority: Priority
    };

    const menuTypeOptions: IRadioButtonItem[] = [
            { text: "Patient", value: MenuNameType.Patient },
            { text: "Practice", value: MenuNameType.Practice },
            { text: "Resource", value: MenuNameType.Resource },
            { text: "Payer", value: MenuNameType.Payer }
        ];

    const priorityOptions: IRadioButtonItem[] = [
            { text: "Low", value: Priority.Low },
            { text: "Normal", value: Priority.Normal },
            { text: "High", value: Priority.High }
        ];

    const defaultPatientKey = ref<ISearchPatientKey>();

    onBeforeMount(async () => {
        compData.manualFileExt = props.filenameExt;
        compData.menuNameId = props.selectedMenuType;
        compData.filename = props.filename;

        // Default to the patient menu type if nothing specified for the component
        if (undefined === compData.menuNameId)
        {
            if (!props.visibleMenuTypes?.length)
            {
                updateSelectedMenuType(MenuNameType.Patient);
            }
            else if (1 == props.visibleMenuTypes.length)
            {
                updateSelectedMenuType(props.visibleMenuTypes[0]);
            }
        }

        privilegesStore.allowAll(AppScreens.PatientSetup).then(allowAllPriv => compData.allowCreatePatient = allowAllPriv);
    });

    function onPatientCreated(patientId: number) {
        compData.showCreateNewPatient = false;
        defaultPatientKey.value = {
            patientId: patientId
        }
    }


    interface ISearchItem {
        searchResultItem?: ISearchPatientItem | ISearchResourceItem | ISearchPayerItem,
        folderItem?: ISearchDocumentFolderItem,
        displaySearchError: boolean,
        displayFolderError: boolean
    }

    function onChangeSearch(searchResultItem: ISearchPatientItem | ISearchResourceItem | ISearchPayerItem, menuNameType: MenuNameType) {
        compData.menuTypeItems[menuNameType].searchResultItem = searchResultItem;
        compData.menuTypeItems[menuNameType].displaySearchError = false;
    }

    function onChangeFolder(folder: ISearchDocumentFolderItem, menuNameType: MenuNameType) {
        compData.menuTypeItems[menuNameType].folderItem = folder;
        compData.menuTypeItems[menuNameType].displayFolderError = false;
    }

    function onSelectReviewedBy(selectedResults: IListByNameExtended[]) {
        compData.reviewList = selectedResults;
    }



    const displayPatientMenu = computed<boolean>(() => compData.menuNameId == MenuNameType.Patient);
    const displayPracticeMenu = computed<boolean>(() => compData.menuNameId == MenuNameType.Practice);
    const displayResourceMenu = computed<boolean>(() => compData.menuNameId == MenuNameType.Resource);
    const displayPayerMenu = computed<boolean>(() => compData.menuNameId == MenuNameType.Payer);
    //const mailSendAttachNameEnabled = computed<boolean>(() => mail.value.imageId != IMAGE_ID_NOT_SAVED);
    const displayFilenameError = computed<boolean>(() => (compData.filename?.length ?? 0) > 0 ? false : true);
    const displayFileExtError = computed<boolean>(() => (compData.manualFileExt?.length ?? 0) > 0 ? false : true);

    const validFilenameExt = computed<string>(() => {
        return fileExtensionWithDot(props.filenameExt) ?? ".pdf";
    });

    const documentFilenameAndExt = computed<string|undefined>(() => {
        if (props.allowManualExtEntry)
            return combineFilenameAndExt(compData.filename, compData.manualFileExt);
        return combineFilenameAndExt(compData.filename, validFilenameExt.value);
    });    

    const patientEncountersEnabled = computed<boolean>(() => {
        if (!displayPatientMenu)
            return false;
        if (undefined === compData.menuTypeItems[MenuNameType.Patient].searchResultItem)
            return false;
        if (undefined === compData.menuTypeItems[MenuNameType.Patient].searchResultItem?.entity_id)
            return false;
        return true;
    });

    // Get the info needed to save EDoc -- unless there is an error, then we return undefined
    function getSaveInfo() : ISaveInfo | undefined
    {
        let bProblem = false;

        const folderMenuNameId = compData.menuNameId;
        if (undefined === folderMenuNameId)
        {
            return;
        }

        let entityId: number|undefined;
        let folderMenuId: number|undefined;

        // Get the entity ID
        if ([MenuNameType.Patient, MenuNameType.Resource, MenuNameType.Payer].includes(folderMenuNameId)) 
        {
            if (compData.menuTypeItems[folderMenuNameId].searchResultItem) {
                entityId = compData.menuTypeItems[folderMenuNameId].searchResultItem?.entity_id;
            }
            if (!entityId) {
                if (!props.hidePatientSearch)
                {
                    compData.menuTypeItems[folderMenuNameId].displaySearchError = true;
                    bProblem = true;
                }
            }
        }

        // Get the folder ID
        if (compData.menuTypeItems[folderMenuNameId].folderItem) {
            folderMenuId = compData.menuTypeItems[folderMenuNameId].folderItem?.folder_menu_id;
        }

        // Make sure we have a folder ID
        if (!folderMenuId) {
            compData.menuTypeItems[folderMenuNameId].displayFolderError = true;
            bProblem = true;
        }

        // Make sure the filename has been entered
        if (!compData.filename?.length) {
            bProblem = true;
        }

        // Don't return if there is a problem
        if (bProblem || undefined === documentFilenameAndExt.value)
            return;

        return {
            entityId: entityId, 
            filename: documentFilenameAndExt.value,
            folderMenuNameId: folderMenuNameId, 
            folderMenuId: folderMenuId,
            receivedDate: compData.receivedDate,
            reviewMessage: compData.reviewMessage,
            reviewList: compData.reviewList?.map(item => ({ identifier: item.identifier, optionalIdentifier: item.optionalIdentifier })),
            priority: compData.priority
        };
    }

    const visibleMenuTypesOptions = computed<IRadioButtonItem[]>(() => {
        if (!props.visibleMenuTypes)
            return menuTypeOptions;

        const visibleItems: IRadioButtonItem[] = [];
        for(const item of props.visibleMenuTypes)
        {
            const radioButtonItem = menuTypeOptions.find(menuItem => item == menuItem.value);
            if (!radioButtonItem)
                continue;
            visibleItems.push(radioButtonItem);
        }
        return visibleItems;
    });

    function updateSelectedMenuType(value: MenuNameType)
    {
        compData.menuNameId = value;
        emits("selectedMenuType", value);
    }

    async function openPatientEncounters() {
        if (!compData.menuTypeItems[MenuNameType.Patient].searchResultItem)
            return;
        const searchResultItem = compData.menuTypeItems[MenuNameType.Patient].searchResultItem;
        if ('patient_id' in searchResultItem) {
            const patientId = searchResultItem.patient_id;
            compData.encounterList = await patientEncountersLast(patientId);
            console.log(compData.encounterList);
            compData.showEncounters = true;
        }
    }

</script>

<template>
    <Row>
        <template #left>
            <Row class="label-isalus-small pe-0" v-show="visibleMenuTypesOptions.length > 1">
                <RadioButtonGroup
                    v-model="compData.menuNameId"
                    :items="visibleMenuTypesOptions"
                    :layout="LayoutOrientation.Horz"
                    @update:model-value="updateSelectedMenuType" />
            </Row>
            <div class="row" v-show="displayPatientMenu && !props.hidePatientSearch">
                <div class="col pe-1">
                    <!-- TODO: Show (readonly) patient name when specified externally -->
                    <SearchPatient
                        :defaultSearchKey="defaultPatientKey" 
                        :addToBaseClass="'label-isalus-small'"
                        @onChange="(val) => onChangeSearch(val, MenuNameType.Patient)">
                    </SearchPatient>
                    <MessageSection v-if="compData.menuTypeItems[MenuNameType.Patient].displaySearchError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                        Please select a Patient
                    </MessageSection>      
                </div>
                <div class="col-auto mt-4 ps-0">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Add"
                        :performAction="async () => compData.showCreateNewPatient = true"
                        :disabled="!compData.allowCreatePatient"
                        :size="ButtonDisplaySize.Small" 
                        :endingPadding="1"
                        text=""
                        tooltip="Create a new patient." />
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Open"
                        :performAction="async () => openPatientEncounters()"
                        :disabled="!patientEncountersEnabled"
                        :size="ButtonDisplaySize.Small" 
                        :endingPadding="1"
                        text=""
                        tooltip="Open encounters for patient." />
                </div>
            </div>
            <Row v-show="displayResourceMenu">
                <SearchResource 
                    :addToBaseClass="'label-isalus-small'"
                    @onChange="(val) => onChangeSearch(val, MenuNameType.Resource)">
                </SearchResource>
                <MessageSection v-if="compData.menuTypeItems[MenuNameType.Resource].displaySearchError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    Please select a Resource
                </MessageSection>      
            </Row>
            <Row v-show="displayPayerMenu">
                <SearchPayer
                    :addToBaseClass="'label-isalus-small'" 
                    @onChange="(val) => onChangeSearch(val, MenuNameType.Payer)">
                </SearchPayer>
                <MessageSection v-if="compData.menuTypeItems[MenuNameType.Payer].displaySearchError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    Please select a Payer
                </MessageSection>
            </Row>
            <Row v-show="displayPracticeMenu">
                <SearchDocumentFolder 
                    v-if="undefined !== compData.menuNameId"
                    :menuNameId="compData.menuNameId" 
                    :addToBaseClass="'label-isalus-small'"
                    placeholder="Search Practice Folders" 
                    @onChange="(val) => onChangeFolder(val, MenuNameType.Practice)">
                </SearchDocumentFolder>
                <MessageSection v-if="compData.menuTypeItems[MenuNameType.Practice].displayFolderError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    Please select a Practice folder
                </MessageSection>
            </Row>
            <Row v-show="displayPatientMenu">
                <SearchDocumentFolder 
                    v-if="undefined !== compData.menuNameId"
                    :menuNameId="compData.menuNameId"
                    :addToBaseClass="'label-isalus-small'" 
                    placeholder="Search Patient Folders" 
                    @onChange="(val) => onChangeFolder(val, MenuNameType.Patient)">
                </SearchDocumentFolder>
                <MessageSection v-if="compData.menuTypeItems[MenuNameType.Patient].displayFolderError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    Please select a Patient folder
                </MessageSection>
            </Row>
            <Row v-show="displayResourceMenu">
                <SearchDocumentFolder 
                    v-if="undefined !== compData.menuNameId"
                    :menuNameId="compData.menuNameId"
                    :addToBaseClass="'label-isalus-small'" 
                    placeholder="Search Resource Folders" 
                    @onChange="(val) => onChangeFolder(val, MenuNameType.Resource)">
                </SearchDocumentFolder>
                <MessageSection v-if="compData.menuTypeItems[MenuNameType.Resource].displayFolderError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    Please select a Resource folder
                </MessageSection>      
            </Row>
            <Row v-show="displayPayerMenu">
                <SearchDocumentFolder 
                    v-if="undefined !== compData.menuNameId"
                    :menuNameId="compData.menuNameId"
                    :addToBaseClass="'label-isalus-small'" 
                    placeholder="Search Payer Folders" 
                    @onChange="(val) => onChangeFolder(val, MenuNameType.Payer)">
                </SearchDocumentFolder>
                <MessageSection v-if="compData.menuTypeItems[MenuNameType.Payer].displayFolderError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    Please select a Payer folder
                </MessageSection>
            </Row>

            <!-- Filename group -->
            <Row :topPadding="2" class="label-isalus-small" :autoSizeRight="true">
                <template #left>
                    Filename
                </template>
                <template #right v-if="hasSlot('FileNameRight')">
                    <slot name="FileNameRight"></slot>    
                </template>
            </Row>
            <Row>
                <TextInput 
                    v-model:text-value="compData.filename" 
                    :maxLength="96"
                    placeholder="Please enter a filename"
                    :acceptCharacter="LimitToFilenameCharacters"
                    cssFramework="vanilla">
                    <template #RightAddon>
                        <MessageText v-if="props.allowManualExtEntry" :displayStyle="DisplayStyle.Danger" title="File extension needs to be manually entered">
                            ???
                        </MessageText>
                        <template v-if="!props.allowManualExtEntry">
                            {{ validFilenameExt }}
                        </template>
                    </template>

                </TextInput>
                <MessageSection v-if="displayFilenameError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    Please enter a filename
                </MessageSection>
            </Row>

            <!-- Manual file extension -->
            <template v-if="props.allowManualExtEntry">
                <Row :topPadding="2" class="label-isalus-small" :autoSizeRight="true">
                    <template #left>
                        File Extension
                    </template>
                </Row>
                <Row>
                    <TextInput 
                        v-model:text-value="compData.manualFileExt" 
                        :maxLength="5"
                        placeholder="Please enter a file extension"
                        :acceptCharacter="LimitToFilenameCharacters"
                        cssFramework="vanilla">
                    </TextInput>
                    <MessageSection v-if="displayFileExtError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                        Please enter a file extension
                    </MessageSection>
                </Row>
            </template>

            <Row class="label-isalus-small">
                Received Date
            </Row>
            <Row>
                <DatePickerNoTZ v-model:value="compData.receivedDate" :clearable="false" />
            </Row>
            <Row v-if="hasSlot('DocumentInfo')" class="label-isalus-small">
                <slot name="DocumentInfo"></slot>
            </Row>
        </template>
        <template #right>
            <Row>
                <SearchUserGroupMultiple 
                    label="To Be Reviewed By" 
                    :defaultListName="ListNames.ReviewedByFavorites"
                    :addToBaseClass="'label-isalus-small'" 
                    @onSelected="onSelectReviewedBy">
                </SearchUserGroupMultiple>
            </Row>
            <Row class="label-isalus-small">
                Review Message
            </Row>
            <Row>
                <TextInput 
                    v-model:text-value="compData.reviewMessage" 
                    :maxLength="1500"
                    cssFramework="vanilla"
                    :rows=3>
                </TextInput>
            </Row>                    
            <Row class="label-isalus-small">
                Priority
            </Row>
            <Row  class="label-isalus-small">
                <RadioButtonGroup 
                    :items="priorityOptions"
                    v-model="compData.priority" />
            </Row>
        </template>
    </Row> 

    <GeneralDialog v-model:show="compData.showCreateNewPatient" :closeOnClickOutside="false">
        <template #title>Create New Patient</template>
        <PatientCreateQuick 
            :saveEnabled="1"
            :resetFields="compData.showCreateNewPatient"
            @onPatientCreated="(val) => onPatientCreated(val)">
        </PatientCreateQuick>
    </GeneralDialog>

    <GeneralDialog v-model:show="compData.showEncounters" :closeOnClickOutside="false">
        <template #title>Patient Encounters</template>
        <ChoiceTable 
            :columns="encounterColumns" 
            :data="compData.encounterList" 
            :show-filters="false" 
            :allowSorting="false"
            fontSize="smaller"
            :key-value-data-member="'appointmentId'" />
    </GeneralDialog>
    
</template>